/**
 * `createTickerLineSource` — turns the `mission.progress` push stream into
 * at most one infobar line per real transition.
 *
 * ⚠ WHY THIS IS STATEFUL, when the plan specified a pure `payload → line`
 * map: `mission.progress` is a **full snapshot**, re-sent on a ~1s
 * leading+trailing throttle. The wire never says "node X just completed"; it
 * says "here is the entire node list now". A stateless mapping therefore
 * re-emits the same sentence every second for the whole mission — the bar
 * would murmur one line over and over instead of reporting news.
 *
 * So this holds the PREVIOUS snapshot per `missionId` and emits only on a
 * diff. An identical snapshot twice yields nothing; that is the first unit
 * test, because it is the failure the plan's original design would have had.
 *
 * The other half the wire cannot give us is the mission's NAME — the payload
 * carries `missionId`, `state`, `nodes[]`, `progressPct`, `ts` and nothing
 * human. The goal lives in the missions store, so the caller passes a lookup
 * and the line joins against it. `null` from that lookup means we do NOT
 * know the mission's name, and the line says so by omitting it rather than
 * fabricating one from the id (a raw id in front of a lawyer is the defect
 * this lane keeps re-catching).
 */
import type { MissionProgressEvent } from "@/stores/useMissionsStore";

export interface TickerLine {
  missionId: string;
  /** ALREADY-LOCALISED and ready to post. */
  text: string;
  mode: "info" | "progress" | "success" | "error";
}

/** What the caller must supply to name a mission and localise a line. */
export interface TickerLineDeps {
  /** The mission's human goal, or null when the store does not know it. */
  goalFor: (missionId: string) => string | null;
  /** i18n. Kept injected so this module stays pure and unit-testable. */
  t: (key: string, vars?: Record<string, unknown>) => string;
}

interface Snapshot {
  state?: string;
  /** node id → state, for diffing which STEP changed. */
  nodeStates: Map<string, string | undefined>;
  /** node id → label, so a completed step can still be named. */
  nodeLabels: Map<string, string | undefined>;
}

const TERMINAL = new Set(["completed", "failed", "canceled"]);

function snapshotOf(ev: MissionProgressEvent): Snapshot {
  const nodeStates = new Map<string, string | undefined>();
  const nodeLabels = new Map<string, string | undefined>();
  for (const n of ev.nodes ?? []) {
    if (!n?.id) continue;
    nodeStates.set(n.id, n.state);
    nodeLabels.set(n.id, n.label);
  }
  return { state: ev.state, nodeStates, nodeLabels };
}

/**
 * `mission` + `step` compose into the operator's requested shape:
 *   "Atualizar o caso: Derivar/atualizar o case-brief do caso"
 * When the goal is unknown the step stands alone rather than being prefixed
 * with an id.
 */
function compose(goal: string | null, step: string | null): string | null {
  if (goal && step) return `${goal}: ${step}`;
  return goal ?? step ?? null;
}

export function createTickerLineSource(deps: TickerLineDeps) {
  const prev = new Map<string, Snapshot>();

  return {
    /** Returns the line to post, or `null` when nothing newsworthy changed. */
    next(ev: MissionProgressEvent): TickerLine | null {
      const missionId = ev.missionId;
      if (!missionId) return null;

      const now = snapshotOf(ev);
      const before = prev.get(missionId);
      prev.set(missionId, now);

      // Evict on a terminal state so a long session does not accumulate a
      // snapshot per mission forever.
      if (now.state && TERMINAL.has(now.state)) {
        prev.delete(missionId);
      }

      const goal = deps.goalFor(missionId);

      // FIRST sighting of a mission: announce it, but only if it is actually
      // under way. A snapshot that arrives already-completed is history, not
      // news — announcing it would tell a lawyer something started when it
      // has in fact just ended.
      if (!before) {
        if (now.state && TERMINAL.has(now.state)) return null;
        const text = compose(goal, null);
        return text ? { missionId, text, mode: "progress" } : null;
      }

      // MISSION-LEVEL transition outranks a step: it is the bigger news.
      if (now.state && now.state !== before.state && TERMINAL.has(now.state)) {
        // ⚠ A TERMINAL WORD NEEDS A SUBJECT. `compose` falls back to the
        // lone verb when the goal is unknown, which renders a bare
        // "concluído" / "falhou" — a status with nothing to attach it to,
        // which is worse than silence because the lawyer cannot tell WHAT
        // finished. (Caught in a live demo, where synthetic ids had no
        // store entry.) A step label can stand alone because it describes
        // itself; "concluído" cannot.
        if (!goal) return null;
        const key =
          now.state === "completed"
            ? "infobar.ticker.missionCompleted"
            : now.state === "failed"
              ? "infobar.ticker.missionFailed"
              : "infobar.ticker.missionCanceled";
        const text = compose(goal, deps.t(key));
        return text
          ? { missionId, text, mode: now.state === "completed" ? "success" : "error" }
          : null;
      }

      // STEP-LEVEL: the first node whose state actually moved. One message
      // per step, which is what the operator asked for.
      for (const [id, state] of now.nodeStates) {
        if (before.nodeStates.get(id) === state) continue;
        const label = now.nodeLabels.get(id);
        if (!label) continue; // unnamed step — nothing worth saying
        const text = compose(goal, label);
        if (!text) continue;
        return {
          missionId,
          text,
          mode: state === "failed" ? "error" : state === "completed" ? "success" : "progress",
        };
      }

      return null;
    },
  };
}
